import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { TableHeader } from '@/types';

export default function useResizableTable({ resizeDelay = 0 }: { resizeDelay?: number } = {}) {
    const headerRefs = ref<Record<string, HTMLElement>>({});
    const headerWidths = ref<Record<string, number>>({});
    const tableRef = ref<HTMLElement | null>(null);
    const observedElements = new Set<Element>();
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => scheduleResize()) : null;
    let delayedResizeTimer: ReturnType<typeof setTimeout> | null = null;
    let requestAnimationFrameId: number | null = null;
    let hasScheduledResize = false;
    let isUnmounted = false;

    function handleResize() {
        Object.entries(headerRefs.value).forEach(([key, element]) => {
            headerWidths.value[key] = element.getBoundingClientRect().width;
        });

        Object.keys(headerWidths.value).forEach((key) => {
            if (!headerRefs.value[key]) {
                delete headerWidths.value[key];
            }
        });
    }

    function scheduleResize() {
        if (isUnmounted || hasScheduledResize) {
            return;
        }

        hasScheduledResize = true;

        nextTick(() => {
            if (isUnmounted) {
                hasScheduledResize = false;
                return;
            }

            requestAnimationFrameId = window.requestAnimationFrame(() => {
                if (isUnmounted) {
                    hasScheduledResize = false;
                    requestAnimationFrameId = null;
                    return;
                }

                handleResize();
                hasScheduledResize = false;
                requestAnimationFrameId = null;
            });
        });
    }

    function observeElement(element: Element | null) {
        if (!resizeObserver || !element || observedElements.has(element)) {
            return;
        }

        resizeObserver.observe(element);
        observedElements.add(element);
    }

    function unobserveElement(element: Element | null) {
        if (!resizeObserver || !element || !observedElements.has(element)) {
            return;
        }

        resizeObserver.unobserve(element);
        observedElements.delete(element);
    }

    function setItemRef(el: Element | null, header: TableHeader) {
        const key = header.value;
        const currentElement = headerRefs.value[key];

        if (currentElement === el) {
            return;
        }

        if (currentElement) {
            unobserveElement(currentElement);
        }

        if (el instanceof HTMLElement) {
            headerRefs.value[key] = el;
            observeElement(el);
        } else {
            delete headerRefs.value[key];
            delete headerWidths.value[key];
        }

        scheduleResize();
    }

    function setTableRef(el: Element | null) {
        if (tableRef.value === el) {
            return;
        }

        if (tableRef.value) {
            unobserveElement(tableRef.value);
        }

        tableRef.value = el instanceof HTMLElement ? el : null;
        observeElement(tableRef.value);
        scheduleResize();
    }

    function getWidth(header: TableHeader): number {
        return headerWidths.value[header.value] || -1;
    }

    onMounted(() => {
        window.addEventListener('resize', scheduleResize);

        observeElement(tableRef.value);
        Object.values(headerRefs.value).forEach((element) => observeElement(element));
        scheduleResize();

        if (resizeDelay) {
            delayedResizeTimer = setTimeout(() => {
                scheduleResize();
            }, resizeDelay);
        }
    });

    onBeforeUnmount(() => {
        isUnmounted = true;
        window.removeEventListener('resize', scheduleResize);

        if (delayedResizeTimer) {
            clearTimeout(delayedResizeTimer);
            delayedResizeTimer = null;
        }

        if (requestAnimationFrameId !== null) {
            window.cancelAnimationFrame(requestAnimationFrameId);
            requestAnimationFrameId = null;
        }

        if (resizeObserver) {
            resizeObserver.disconnect();
        }

        observedElements.clear();
    });

    return {
        handleResize,
        scheduleResize,
        setTableRef,
        setItemRef,
        getWidth,
    };
}
